import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiCalendar, FiFilter, FiFileText } from 'react-icons/fi';
import { reportesService, clientesService, productosService } from '../../services';
import useReportExport from '../../hooks/useReportExport';
import Autocomplete from '../../components/UI/Autocomplete';
import './ReportesCommon.scss';

const ReportesRetornos = () => {
  const navigate = useNavigate();
  const { exportToPDF } = useReportExport();
  const [activeTab, setActiveTab] = useState('general'); // general, por-producto, por-cliente
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Filtros
  const [period, setPeriod] = useState('month'); // week, month, quarter, year
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');

  useEffect(() => {
    loadReportData();
  }, [activeTab, period, startDate, endDate, selectedClient, selectedProduct]);

  // Función de búsqueda de clientes para Autocomplete
  const searchClientes = async (searchTerm) => {
    try {
      const response = await clientesService.getClientes({
        is_active: true,
        search: searchTerm,
        limit: 50
      });
      if (response.success) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error al buscar clientes:', error);
      return [];
    }
  };

  // Función de búsqueda de productos para Autocomplete
  const searchProductos = async (searchTerm) => {
    try {
      const response = await productosService.getProductos({
        status: 'ACTIVE',
        search: searchTerm,
        limit: 50
      });
      if (response.success) {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error al buscar productos:', error);
      return [];
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };

      // Agregar filtros específicos según el tab activo
      if (activeTab === 'por-cliente' && selectedClient) {
        params.client_id = selectedClient;
      } else if (activeTab === 'por-producto' && selectedProduct) {
        params.product_id = selectedProduct;
      }

      const response = await reportesService.getRetornos(params);

      if (response.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error al cargar reporte de retornos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    const tabLabel = tabs.find(t => t.id === activeTab)?.label || 'General';
    exportToPDF(`Reporte de Retornos - ${tabLabel}`);
  };

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'por-producto', label: 'Por Producto' },
    { id: 'por-cliente', label: 'Por Cliente' }
  ];

  const periodOptions = [
    { value: 'week', label: 'Semana Actual' },
    { value: 'month', label: 'Mes Actual' },
    { value: 'quarter', label: 'Trimestre Actual' },
    { value: 'year', label: 'Año Actual' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  return (
    <div className="reportes-detail-page">
      {/* Header */}
      <div className="reportes-detail-header">
        <div className="header-top">
          <button className="btn-back" onClick={() => navigate('/system/reportes')}>
            <FiArrowLeft />
            <span>Regresar a Reportes</span>
          </button>
          <h1 className="reportes-detail-title">Reporte de Retornos</h1>
        </div>

        <div className="header-actions">
          <button className="btn-export" onClick={handleExportPDF}>
            <FiFileText />
            <span>Exportar PDF</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="reportes-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="reportes-filters">
        <div className="filter-group">
          <label className="filter-label">
            <FiCalendar />
            <span>Período</span>
          </label>
          <select
            className="filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Desde</label>
          <input
            type="date"
            className="filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Hasta</label>
          <input
            type="date"
            className="filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {activeTab === 'por-cliente' && (
          <div className="filter-group">
            <label className="filter-label">
              <FiFilter />
              <span>Cliente</span>
            </label>
            <Autocomplete
              value={selectedClient}
              onChange={setSelectedClient}
              onSearch={searchClientes}
              getOptionLabel={(cliente) => cliente.company_name || cliente.full_name || `Cliente #${cliente.id}`}
              getOptionValue={(cliente) => cliente.id}
              placeholder="Buscar cliente..."
              noResultsText="No se encontraron clientes"
            />
          </div>
        )}

        {activeTab === 'por-producto' && (
          <div className="filter-group">
            <label className="filter-label">
              <FiFilter />
              <span>Producto</span>
            </label>
            <Autocomplete
              value={selectedProduct}
              onChange={setSelectedProduct}
              onSearch={searchProductos}
              getOptionLabel={(producto) => `${producto.code} - ${producto.name}`}
              getOptionValue={(producto) => producto.id}
              placeholder="Buscar producto..."
              noResultsText="No se encontraron productos"
            />
          </div>
        )}
      </div>

      {/* Resumen */}
      {reportData?.summary && (
        <div className="reportes-summary">
          <div className="summary-card">
            <span className="summary-label">Total Retornos</span>
            <span className="summary-value">{reportData.summary.total_returns}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Productos Retornados</span>
            <span className="summary-value">{reportData.summary.total_items_returned}</span>
          </div>
          <div className="summary-card discount">
            <span className="summary-label">Monto Total</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_amount)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Tasa de Retorno</span>
            <span className="summary-value">{formatPercentage(reportData.summary.return_rate)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Clientes Únicos</span>
            <span className="summary-value">{reportData.summary.unique_clients || 0}</span>
          </div>
        </div>
      )}

      {/* Tabla de Desglose */}
      <div className="reportes-table-container">
        {loading ? (
          <div className="loading-state">Cargando reporte...</div>
        ) : reportData?.breakdown && reportData.breakdown.length > 0 ? (
          <table className="reportes-table">
            <thead>
              <tr>
                {activeTab === 'general' && <th>Período</th>}
                {activeTab === 'por-cliente' && <th>Cliente</th>}
                {activeTab === 'por-producto' && <th>Producto</th>}
                <th className="text-right">Retornos</th>
                <th className="text-right">Unidades</th>
                <th className="text-right">Monto Total</th>
                <th className="text-right">Monto Promedio</th>
                <th className="text-right">Tasa de Retorno</th>
                {activeTab !== 'por-cliente' && <th>Motivo Principal</th>}
              </tr>
            </thead>
            <tbody>
              {reportData.breakdown.map((row, index) => (
                <tr key={index}>
                  {activeTab === 'general' && <td className="period-cell">{row.period_label}</td>}
                  {activeTab === 'por-cliente' && <td className="font-bold">{row.client_name}</td>}
                  {activeTab === 'por-producto' && <td className="font-bold">{row.product_name}</td>}
                  <td className="text-right">{row.return_count}</td>
                  <td className="text-right">{row.items_returned}</td>
                  <td className="text-right discount">{formatCurrency(row.total_amount)}</td>
                  <td className="text-right">{formatCurrency(row.avg_amount)}</td>
                  <td className="text-right">{formatPercentage(row.return_rate)}</td>
                  {activeTab !== 'por-cliente' && (
                    <td>
                      <span className="reason-badge">
                        {row.top_reason || 'No especificado'}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay retornos registrados para el período seleccionado.</p>
          </div>
        )}
      </div>

      {/* Tabla de Motivos de Retorno (solo en vista general) */}
      {activeTab === 'general' && reportData?.reasons && reportData.reasons.length > 0 && (
        <div className="reportes-table-container" style={{ marginTop: '30px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#4a3470',
            marginBottom: '15px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Motivos de Retorno
          </h3>
          <table className="reportes-table">
            <thead>
              <tr>
                <th>Motivo</th>
                <th className="text-right">Cantidad</th>
                <th className="text-right">Porcentaje</th>
                <th className="text-right">Monto Total</th>
              </tr>
            </thead>
            <tbody>
              {reportData.reasons.map((reason, index) => (
                <tr key={index}>
                  <td>
                    <span className="reason-badge">{reason.reason}</span>
                  </td>
                  <td className="text-right font-bold">{reason.count}</td>
                  <td className="text-right">{formatPercentage(reason.percentage)}</td>
                  <td className="text-right">{formatCurrency(reason.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportesRetornos;
